import React, { useContext, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AuthContext } from './AuthContext';
import './Dashboard.css'; // Ensure you have styles for left-aligning

const Dashboard = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [vetBills, setVetBills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteError, setDeleteError] = useState('');
  const [deletingId, setDeletingId] = useState('');
  const [visibilityTarget, setVisibilityTarget] = useState(null);
  const [visibilityError, setVisibilityError] = useState('');
  const [updatingVisibilityId, setUpdatingVisibilityId] = useState('');
  const [pets, setPets] = useState([]);
  const [showPetForm, setShowPetForm] = useState(false);
  const [petForm, setPetForm] = useState({ name: '', species: '', breed: '' });
  const [petError, setPetError] = useState('');
  const [petSaving, setPetSaving] = useState(false);
  const [deletePetTarget, setDeletePetTarget] = useState(null);
  const [attachingBillId, setAttachingBillId] = useState('');

  useEffect(() => {
    setLoading(true);
    setLoadError('');

    fetch('/api/vetbills', {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}` // Adjust if using token-based auth
      }
    })
      .then(async response => {
        const data = await response.json();
        if (!response.ok) {
          throw new Error(data.message || 'Unable to load your uploads');
        }
        setVetBills(data);
      })
      .catch(error => {
        console.error('Error fetching vet bills:', error);
        setLoadError(error.message);
      })
      .finally(() => setLoading(false));

    fetch('/api/pets', {
      headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
    })
      .then(response => (response.ok ? response.json() : []))
      .then(data => setPets(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, []);

  const petById = (petId) => pets.find(pet => String(pet._id) === String(petId));

  const handleAddPet = async (event) => {
    event.preventDefault();
    if (!petForm.name.trim()) {
      setPetError('Please give the pet a name.');
      return;
    }
    setPetSaving(true);
    setPetError('');
    try {
      const response = await fetch('/api/pets', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(petForm)
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'Unable to create pet profile');
      }
      setPets(current => [...current, data.pet]);
      setPetForm({ name: '', species: '', breed: '' });
      setShowPetForm(false);
    } catch (error) {
      setPetError(error.message);
    } finally {
      setPetSaving(false);
    }
  };

  const handleDeletePet = async () => {
    if (!deletePetTarget) return;
    setPetSaving(true);
    setPetError('');
    try {
      const response = await fetch(`/api/pets/${deletePetTarget._id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'Unable to delete pet profile');
      }
      setPets(current => current.filter(pet => pet._id !== deletePetTarget._id));
      setVetBills(current => current.map(bill => (
        String(bill.pet) === String(deletePetTarget._id) ? { ...bill, pet: null } : bill
      )));
      setDeletePetTarget(null);
    } catch (error) {
      setPetError(error.message);
    } finally {
      setPetSaving(false);
    }
  };

  const handleAttachPet = async (bill, petId) => {
    setAttachingBillId(bill._id);
    try {
      const response = await fetch(`/api/vetbills/${bill._id}/pet`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ petId: petId || null })
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'Unable to update pet attachment');
      }
      setVetBills(current => current.map(b => (b._id === bill._id ? { ...b, pet: petId || null } : b)));
      setPets(current => current.map(pet => {
        const wasAttached = String(bill.pet) === String(pet._id);
        const isAttached = String(petId) === String(pet._id);
        if (wasAttached === isAttached) return pet;
        return { ...pet, uploadCount: Math.max(0, (pet.uploadCount || 0) + (isAttached ? 1 : -1)) };
      }));
    } catch (error) {
      console.error('Error attaching pet:', error);
    } finally {
      setAttachingBillId('');
    }
  };

  const getDocumentTypeLabel = (documentType) => {
    const value = String(documentType || 'upload').toLowerCase();
    if (value === 'bill') return 'Bill';
    if (value === 'estimate') return 'Estimate';
    return 'Upload';
  };

  const getUploadCounts = () => {
    const bills = vetBills.filter(bill => String(bill.documentType || '').toLowerCase() === 'bill').length;
    const estimates = vetBills.filter(bill => String(bill.documentType || '').toLowerCase() === 'estimate').length;

    return { bills, estimates };
  };

  const handleDelete = async () => {
    if (!deleteTarget) {
      return;
    }

    setDeletingId(deleteTarget._id);
    setDeleteError('');

    try {
      const response = await fetch(`/api/vetbills/${deleteTarget._id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Unable to delete upload');
      }

      setVetBills(currentBills => currentBills.filter(bill => bill._id !== deleteTarget._id));
      setDeleteTarget(null);
    } catch (error) {
      setDeleteError(error.message);
    } finally {
      setDeletingId('');
    }
  };

  const isPrivate = (bill) => bill.visibility === 'private';

  const handleVisibilityChange = async () => {
    if (!visibilityTarget) {
      return;
    }

    const makingPublic = isPrivate(visibilityTarget);
    setUpdatingVisibilityId(visibilityTarget._id);
    setVisibilityError('');

    try {
      const response = await fetch(`/api/vetbills/${visibilityTarget._id}/visibility`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          visibility: makingPublic ? 'public' : 'private',
          ...(makingPublic ? { privacyConfirmed: true } : {})
        })
      });
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Unable to update visibility');
      }

      setVetBills(currentBills => currentBills.map(bill => (
        bill._id === visibilityTarget._id ? { ...bill, visibility: data.visibility } : bill
      )));
      setVisibilityTarget(null);
    } catch (error) {
      setVisibilityError(error.message);
    } finally {
      setUpdatingVisibilityId('');
    }
  };

  const uploadCounts = getUploadCounts();
  const privateCount = vetBills.filter(isPrivate).length;

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <div>
          {user && <span className="dashboard-eyebrow">{`${user.firstName}'s account`}</span>}
          <h2>My Vet Bills</h2>
          <p>Manage the bills and estimates you have contributed to VetBill.</p>
        </div>
        <Link className="dashboard-upload-link" to="/upload/step1">Upload a bill</Link>
      </div>

      <div className="dashboard-summary">
        <div>
          <span>Total uploads</span>
          <strong>{vetBills.length}</strong>
        </div>
        <div>
          <span>Bills</span>
          <strong>{uploadCounts.bills}</strong>
        </div>
        <div>
          <span>Estimates</span>
          <strong>{uploadCounts.estimates}</strong>
        </div>
        <div>
          <span>Private records</span>
          <strong>{privateCount}</strong>
        </div>
      </div>

      <div className="dashboard-section-heading">
        <h3>My pets</h3>
        <p>Attach uploads to a pet to build a private history of visits and costs. Pet profiles are never shown publicly.</p>
      </div>

      <div className="pets-panel">
        {pets.length === 0 && !showPetForm && (
          <span className="muted-text">No pet profiles yet.</span>
        )}
        <div className="pets-chip-row">
          {pets.map(pet => (
            <span key={pet._id} className="pet-chip">
              <Link to={`/pets/${pet._id}`} className="pet-chip-link" title={`View ${pet.name}'s visit history`}>
                <strong>{pet.name}</strong>
                <span>{[pet.species, pet.breed].filter(Boolean).join(' · ') || 'pet'}</span>
                <span>{pet.uploadCount || 0} upload{(pet.uploadCount || 0) === 1 ? '' : 's'}</span>
              </Link>
              <button type="button" aria-label={`Delete ${pet.name}`} onClick={() => { setPetError(''); setDeletePetTarget(pet); }}>×</button>
            </span>
          ))}
          {!showPetForm && (
            <button type="button" className="pet-add-button" onClick={() => { setPetError(''); setShowPetForm(true); }}>
              + Add pet
            </button>
          )}
        </div>
        {showPetForm && (
          <form className="pet-add-form" onSubmit={handleAddPet}>
            <input placeholder="Name" value={petForm.name} onChange={e => setPetForm(f => ({ ...f, name: e.target.value }))} />
            <input placeholder="Species (dog, cat...)" value={petForm.species} onChange={e => setPetForm(f => ({ ...f, species: e.target.value }))} />
            <input placeholder="Breed (optional)" value={petForm.breed} onChange={e => setPetForm(f => ({ ...f, breed: e.target.value }))} />
            <button type="submit" className="next-btn" disabled={petSaving || !petForm.name.trim()}>{petSaving ? 'Saving...' : 'Save pet'}</button>
            <button type="button" className="secondary-btn" onClick={() => setShowPetForm(false)} disabled={petSaving}>Cancel</button>
          </form>
        )}
        {petError && !deletePetTarget && <p className="delete-error">{petError}</p>}
      </div>

      <div className="dashboard-section-heading">
        <h3>Your uploads</h3>
        <p>Private records are visible only to you. Deleting an upload removes it everywhere.</p>
      </div>

      {loading ? (
        <div className="dashboard-empty-card">Loading your uploads...</div>
      ) : loadError ? (
        <div className="dashboard-empty-card error-card">{loadError}</div>
      ) : vetBills.length === 0 ? (
        <div className="dashboard-empty-card">
          <strong>No uploads yet.</strong>
          <span>Upload a bill or estimate to help build real veterinary price comparisons.</span>
        </div>
      ) : (
        <ul className="uploads-list">
          {vetBills.map(bill => (
            <li key={bill._id} className="upload-list-item">
              <Link to={`/bills/${bill._id}`} className="upload-item">
                <img src={bill.thumbnailUrl || bill.imageUrl} alt={`${getDocumentTypeLabel(bill.documentType)} thumbnail`} className="upload-thumbnail" />
                <span className="upload-main">
                  <strong>
                    <span className="upload-hospital-name">{bill.vetHospital || 'Unknown hospital'}</span>
                    {isPrivate(bill) && <span className="private-badge">Private</span>}
                    {bill.pet && petById(bill.pet) && <span className="pet-badge">{petById(bill.pet).name}</span>}
                  </strong>
                  <span>{getDocumentTypeLabel(bill.documentType)} - {new Date(bill.billDate).toLocaleDateString()}</span>
                  <span>{bill.services?.length || 0} services</span>
                </span>
              </Link>
              <div className="upload-row-actions">
                {pets.length > 0 && (
                  <select
                    className="pet-attach-select"
                    value={bill.pet || ''}
                    disabled={attachingBillId === bill._id}
                    onChange={e => handleAttachPet(bill, e.target.value)}
                    aria-label="Attach to pet"
                  >
                    <option value="">No pet</option>
                    {pets.map(pet => (
                      <option key={pet._id} value={pet._id}>{pet.name}</option>
                    ))}
                  </select>
                )}
                <button type="button" className="visibility-toggle-button" onClick={() => { setVisibilityError(''); setVisibilityTarget(bill); }}>
                  {isPrivate(bill) ? 'Make public' : 'Make private'}
                </button>
                <Link to={`/bills/${bill._id}/privacy`} className="privacy-upload-button">
                  Review privacy
                </Link>
                <button type="button" className="delete-upload-button" onClick={() => setDeleteTarget(bill)}>
                  Delete
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {visibilityTarget && (
        <div className="delete-confirmation-backdrop" role="presentation">
          <div className="delete-confirmation" role="dialog" aria-modal="true" aria-labelledby="visibility-change-title">
            <h3 id="visibility-change-title">
              {isPrivate(visibilityTarget) ? 'Make this upload public?' : 'Make this upload private?'}
            </h3>
            <p>
              {isPrivate(visibilityTarget)
                ? 'The document image, services, and prices will show up where pet owners compare prices. We recommend checking the image and hiding any personal info first.'
                : 'It will be removed from public comparison data and hospital pages. Only you will be able to see it.'}
            </p>
            <div className="delete-upload-summary">
              <strong>{visibilityTarget.vetHospital || 'Unknown hospital'}</strong>
              <span>{new Date(visibilityTarget.billDate).toLocaleDateString()}</span>
              <span>{visibilityTarget.services?.length || 0} services</span>
            </div>
            {visibilityError && <p className="delete-error">{visibilityError}</p>}
            <div className="delete-confirmation-actions">
              <button type="button" onClick={() => setVisibilityTarget(null)} disabled={Boolean(updatingVisibilityId)}>
                Cancel
              </button>
              {isPrivate(visibilityTarget) ? (
                <>
                  <button
                    type="button"
                    onClick={handleVisibilityChange}
                    disabled={updatingVisibilityId === visibilityTarget._id}
                  >
                    {updatingVisibilityId === visibilityTarget._id ? 'Updating...' : 'It is already clean — publish now'}
                  </button>
                  <button
                    type="button"
                    className="confirm-visibility-button"
                    onClick={() => navigate(`/bills/${visibilityTarget._id}/privacy?makePublic=1`)}
                    disabled={Boolean(updatingVisibilityId)}
                  >
                    Check &amp; hide info first
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  className="confirm-visibility-button"
                  onClick={handleVisibilityChange}
                  disabled={updatingVisibilityId === visibilityTarget._id}
                >
                  {updatingVisibilityId === visibilityTarget._id ? 'Updating...' : 'Make private'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {deletePetTarget && (
        <div className="delete-confirmation-backdrop" role="presentation">
          <div className="delete-confirmation" role="dialog" aria-modal="true" aria-labelledby="delete-pet-title">
            <h3 id="delete-pet-title">Delete {deletePetTarget.name}'s profile?</h3>
            <p>
              Uploads attached to {deletePetTarget.name} are kept — they just become unassigned. Only the pet profile is removed.
            </p>
            {petError && <p className="delete-error">{petError}</p>}
            <div className="delete-confirmation-actions">
              <button type="button" onClick={() => setDeletePetTarget(null)} disabled={petSaving}>
                Cancel
              </button>
              <button type="button" className="confirm-delete-button" onClick={handleDeletePet} disabled={petSaving}>
                {petSaving ? 'Deleting...' : 'Delete pet profile'}
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteTarget && (
        <div className="delete-confirmation-backdrop" role="presentation">
          <div className="delete-confirmation" role="dialog" aria-modal="true" aria-labelledby="delete-upload-title">
            <h3 id="delete-upload-title">Delete this upload?</h3>
            <p>
              This removes the {getDocumentTypeLabel(deleteTarget.documentType).toLowerCase()} from your account, public hospital pages, and comparison results.
            </p>
            <div className="delete-upload-summary">
              <strong>{deleteTarget.vetHospital || 'Unknown hospital'}</strong>
              <span>{new Date(deleteTarget.billDate).toLocaleDateString()}</span>
              <span>{deleteTarget.services?.length || 0} services</span>
            </div>
            {deleteError && <p className="delete-error">{deleteError}</p>}
            <div className="delete-confirmation-actions">
              <button type="button" onClick={() => setDeleteTarget(null)} disabled={Boolean(deletingId)}>
                Cancel
              </button>
              <button type="button" className="confirm-delete-button" onClick={handleDelete} disabled={deletingId === deleteTarget._id}>
                {deletingId === deleteTarget._id ? 'Deleting...' : 'Delete upload'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
